import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Trash2 } from "lucide-react";
import { useDataProvider, useNotify } from "ra-core";
import {
  planOneDoor,
  planOneViaSupabase,
  queuedMessage,
  unplanOne,
  unplanOneViaSupabase,
} from "./sendOneCardRules";
import { useCallback, useState } from "react";

import { SendDialog } from "./SendDialog";

/**
 * One card, one door, on demand.
 *
 * WHY THIS EXISTS SEPARATELY FROM A DROP
 * --------------------------------------
 * The campaign mails by cell, but a single card is a real and frequent need:
 * proving a printer with a piece you can physically hold, or answering someone
 * who asked for one. Doing it through the drop planner means planning 285 rows
 * and sending one, which leaves 284 rows nobody meant to create -- and those
 * rows then read as "this cell is planned" on every screen that counts them.
 *
 * IT IS NOT A BACK DOOR
 * ---------------------
 * `plan_one_door()` selects from `mailable_doors`, exactly as `plan_drop()`
 * does, so an opted-out address, a retired door or the holdout is unreachable
 * here too. The card is an ordinary `mailings` row: it counts against the
 * door's one-per-touch limit and appears in that company's mail history. A
 * side channel that skipped the ledger would make the door look un-mailed to
 * everything that matters -- including the next drop, which would then mail it
 * again.
 */

type Door = {
  id: number;
  mail_to: string | null;
  street: string;
  city: string | null;
  state: string | null;
  zip5: string;
  cell: string;
  company_name: string | null;
  touches_sent: number;
  last_touch: string | null;
};

const TOUCHES = Array.from(
  { length: 6 },
  (_, i) => `T${String(i + 1).padStart(2, "0")}`,
);

export const SendOneCard = ({ onSent }: { onSent?: () => void }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [q, setQ] = useState("");
  const [hits, setHits] = useState<Door[] | null>(null);
  const [picked, setPicked] = useState<Door | null>(null);
  const [touch, setTouch] = useState("T01");
  const [busy, setBusy] = useState(false);
  const [mailingId, setMailingId] = useState<number | null>(null);

  const search = useCallback(async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      // NOT `filter: { q }`. The provider only turns `q` into a search for the
      // few resources that register a lifecycle callback for it (contacts,
      // companies, deals); for anything else it is passed straight through as
      // a column named "q", and PostgREST rejects the request. The `@or` /
      // `@ilike` shape below is what that callback actually produces, so this
      // is the same search, spelled explicitly.
      const { data } = await dataProvider.getList("door_directory", {
        filter: {
          is_mailable: true,
          "@or": {
            "mail_to@ilike": q.trim(),
            "street@ilike": q.trim(),
            "city@ilike": q.trim(),
          },
        },
        pagination: { page: 1, perPage: 20 },
        sort: { field: "mail_to", order: "ASC" },
      });
      setHits(data as Door[]);
    } catch (e) {
      notify(String((e as Error)?.message ?? e), { type: "error" });
    } finally {
      setBusy(false);
    }
  }, [dataProvider, notify, q]);

  /**
   * Queue the card, but do not send it.
   *
   * Two steps rather than one on purpose: this writes a reversible row and the
   * dialog spends the money. Collapsing them would make picking an address from
   * a search result the same gesture as buying postage for it.
   */
  const queue = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      const card = await planOneDoor(planOneViaSupabase, picked.id, touch);
      setMailingId(card.mailingId);
      notify(queuedMessage(card, touch, picked.mail_to), { type: "info" });
      onSent?.();
    } catch (e) {
      // plan_one_door raises for the holdout, an opted-out door and a bad
      // touch; readPlanResult raises for a reply that carries no usable
      // mailing_id. Both are the person's business.
      notify((e as Error).message, { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  /**
   * Remove the queued card again.
   *
   * Its own RPC rather than `unplan_drop`, which removes every planned card in
   * the cell and touch -- correct for abandoning a drop, catastrophic for
   * undoing one card queued by hand. `unplan_one` refuses anything already
   * sent, so this can only ever discard something that has cost nothing.
   */
  const unqueue = async () => {
    if (mailingId === null) return;
    setBusy(true);
    try {
      await unplanOne(unplanOneViaSupabase, mailingId);
      setMailingId(null);
      notify("Card removed from the queue.", { type: "info" });
      onSent?.();
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent className="pt-4 space-y-3">
        <div>
          <h2 className="text-sm font-medium">Send a single card</h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            One door, one postcard, either printer. It becomes an ordinary
            mailing — it counts against that door's one-card-per-touch limit and
            shows in the company's mail history, so the next drop will not send
            the same card again.
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-56">
            <div className="text-xs text-muted-foreground mb-1">
              Find a door — name, street or city
            </div>
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder="e.g. LOGAN VINEYARDS"
            />
          </div>
          <Button
            variant="outline"
            onClick={search}
            disabled={busy || !q.trim()}
          >
            <Search className="size-4" /> Search
          </Button>
        </div>

        {hits && !hits.length && (
          <p className="text-xs text-muted-foreground">
            No mailable door matches that. Opted-out and retired doors are not
            searchable here — they cannot be mailed individually either.
          </p>
        )}

        {hits && hits.length > 0 && (
          <div className="border rounded-md divide-y max-h-64 overflow-y-auto">
            {hits.map((d) => (
              <button
                key={d.id}
                onClick={() => {
                  setPicked(d);
                  setMailingId(null);
                }}
                className={`w-full text-left p-2 text-sm hover:bg-muted ${
                  picked?.id === d.id ? "bg-muted" : ""
                }`}
              >
                <div className="font-medium">
                  {d.mail_to ?? d.company_name ?? "—"}
                </div>
                <div className="text-xs text-muted-foreground">
                  {d.street}, {d.city} {d.state} {d.zip5} · {d.cell}
                  {d.touches_sent > 0 &&
                    ` · ${d.touches_sent} sent (last ${d.last_touch})`}
                </div>
              </button>
            ))}
          </div>
        )}

        {picked && (
          <div className="flex flex-wrap items-end gap-2 border-t pt-3">
            <div className="text-sm">
              <div className="font-medium">{picked.mail_to ?? "—"}</div>
              <div className="text-xs text-muted-foreground">
                {picked.street}, {picked.city} {picked.state} {picked.zip5}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Postcard</div>
              <Select
                value={touch}
                onValueChange={(v) => {
                  setTouch(v);
                  setMailingId(null);
                }}
              >
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TOUCHES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {mailingId === null ? (
              <Button onClick={queue} disabled={busy}>
                Queue this card
              </Button>
            ) : (
              <>
                <Badge variant="outline">queued</Badge>
                <SendDialog
                  mailingIds={[mailingId]}
                  count={1}
                  label="Send or test this card"
                  onDone={() => {
                    setMailingId(null);
                    onSent?.();
                  }}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={unqueue}
                  title="Remove this card from the queue"
                >
                  <Trash2 className="size-4" /> Remove
                </Button>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
